import { RowHeader, TableCardRow, TableCardMobileStack, TABLE_CARD_BODY_CELL } from "@/components/Table";
import { renderEmptyCellState } from "@/components/Table/utils";
import { formatDate } from "@/pages/_layouts/jobs/_utils";
import { useNavigate } from "@/router";
import { UNTITLED_CAREER_PLAN_TITLE } from "./_CareerPlanTitle";
import {
  groupHubCareerPlans,
  HUB_CAREER_PLAN_COMPLETED_LABEL,
  HUB_CAREER_PLAN_DRAFTS_LABEL,
  HUB_CAREER_PLAN_PUBLISHED_LABEL,
  type HubCareerPlan
} from "./careerPlanHubSummaries";

const COLUMN_COUNT = 2;

function planDisplayTitle(title: string | null) {
  return title?.trim() || UNTITLED_CAREER_PLAN_TITLE;
}

function CareerPlanListRow({ plan, pgaId }: { plan: HubCareerPlan; pgaId?: string }) {
  const navigate = useNavigate();
  const title = planDisplayTitle(plan.title);
  const lastModified = plan.updatedAt ? formatDate(plan.updatedAt) : "";

  return (
    <TableCardRow
      columnCount={COLUMN_COUNT}
      rowTestId={`career-plan-row-${plan.id}`}
      onClick={
        pgaId
          ? () =>
              navigate("/people/:pgaId/career/career-profile/plans/:planId", {
                params: { pgaId, planId: plan.id }
              })
          : undefined
      }
      ariaLabel={`Open ${title}`}
      mobile={<TableCardMobileStack lines={[title, lastModified || renderEmptyCellState(null)]} />}
    >
      <td className={TABLE_CARD_BODY_CELL}>{title}</td>
      <td className={`${TABLE_CARD_BODY_CELL} whitespace-nowrap text-sys-brand-on-surface-variant`}>
        {lastModified || renderEmptyCellState(null)}
      </td>
    </TableCardRow>
  );
}

function CareerPlanGroup({
  label,
  plans,
  pgaId,
  rowTestId
}: {
  label: string;
  plans: HubCareerPlan[];
  pgaId?: string;
  rowTestId: string;
}) {
  if (plans.length === 0) return null;

  return (
    <>
      <RowHeader label={label} columnCount={COLUMN_COUNT} rowTestId={rowTestId} />
      {plans.map((plan) => (
        <CareerPlanListRow key={plan.id} plan={plan} pgaId={pgaId} />
      ))}
    </>
  );
}

export default function CareerPlanListTable({ plans, pgaId }: { plans: HubCareerPlan[]; pgaId?: string }) {
  const { drafts, published, completed } = groupHubCareerPlans(plans);

  return (
    <table className="w-full" data-testid="career-plan-table">
      <colgroup>
        <col />
        <col className="w-28" />
      </colgroup>
      <tbody>
        <CareerPlanGroup
          label={HUB_CAREER_PLAN_DRAFTS_LABEL}
          plans={drafts}
          pgaId={pgaId}
          rowTestId="career-plan-drafts-header"
        />
        <CareerPlanGroup
          label={HUB_CAREER_PLAN_PUBLISHED_LABEL}
          plans={published}
          pgaId={pgaId}
          rowTestId="career-plan-published-header"
        />
        <CareerPlanGroup
          label={HUB_CAREER_PLAN_COMPLETED_LABEL}
          plans={completed}
          pgaId={pgaId}
          rowTestId="career-plan-completed-header"
        />
      </tbody>
    </table>
  );
}
